import React, { useState } from 'react';
import './VehicleApplicationForm.css';
import { supabase } from '../supabase';

const VehicleApplicationForm = () => {
  const [applicationType, setApplicationType] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    businessName: '',
    residentialAddress: '',
    phone: '',
    email: '',
    occupation: '',
    identificationType: '',
    idNumber: '',
    driversLicence: '',
    licenceExpiry: '',
    preferredVehicle: '',
    makeModel: '',
    year: '',
    intendedUse: '',
    primaryUseArea: '',
    commencementDate: '',
    leasePeriod: '',
    leasePayment: '',
    leasePaymentFrequency: '',
    securityDeposit: '',
    maintenanceResponsibility: '',
    returnDate: '',
    purchasePrice: '',
    initialDeposit: '',
    balance: '',
    repaymentPeriod: '',
    repaymentAmount: '',
    repaymentFrequency: '',
    repaymentSource: '',
    monthlyIncome: '',
    guarantorName: '',
    guarantorPhone: '',
    guarantorAddress: '',
    guarantorOccupation: '',
    guarantorRelationship: '',
    guarantorIdType: '',
    guarantorIdNumber: '',
    applicantName: '',
    applicantSignature: '',
    applicantDate: '',
    guarantorNameSigned: '',
    guarantorSignature: '',
    guarantorDate: '',
    agreeTerms: false
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [applicantIdFile, setApplicantIdFile] = useState(null);
  const [applicantIdPreview, setApplicantIdPreview] = useState('');
  const [guarantorIdFile, setGuarantorIdFile] = useState(null);
  const [guarantorIdPreview, setGuarantorIdPreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formStatus, setFormStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState({ title: '', message: '', type: '' });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleApplicationTypeChange = (type) => {
    setApplicationType(type);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should not exceed 5MB.');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleApplicantIdChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should not exceed 5MB.');
        return;
      }
      setApplicantIdFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setApplicantIdPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGuarantorIdChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should not exceed 5MB.');
        return;
      }
      setGuarantorIdFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setGuarantorIdPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadFile = async (file, folder) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${folder}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
    const filePath = `${folder}/${fileName}`;

    const { error } = await supabase.storage
      .from('applicant-documents')
      .upload(filePath, file);

    if (error) {
      console.error('Upload error:', error);
      throw error;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('applicant-documents')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const validateForm = () => {
    // Check application type is selected
    if (!applicationType) {
      setPopupMessage({
        title: '⚠️ Application Type Required',
        message: 'Please select either VEHICLE LEASE or HIRE PURCHASE before submitting.',
        type: 'error'
      });
      setShowPopup(true);
      return false;
    }

    const required = ['fullName', 'phone', 'email', 'occupation', 'identificationType', 'idNumber', 'preferredVehicle', 'intendedUse'];
    for (const field of required) {
      if (!formData[field]) return false;
    }

    if (applicationType === 'lease') {
      if (!formData.leasePeriod || !formData.leasePayment) return false;
    } else if (applicationType === 'purchase') {
      if (!formData.purchasePrice || !formData.initialDeposit || !formData.repaymentPeriod) return false;
    }

    if (!formData.guarantorName || !formData.guarantorPhone) return false;
    if (!formData.applicantName || !formData.applicantDate) return false;
    if (!photoFile) return false;
    if (!applicantIdFile) return false;
    if (!guarantorIdFile) return false;

    return true;
  };

  const closePopup = () => {
    setShowPopup(false);
    if (formStatus === 'success') {
      setFormData({
        fullName: '',
        businessName: '',
        residentialAddress: '',
        phone: '',
        email: '',
        occupation: '',
        identificationType: '',
        idNumber: '',
        driversLicence: '',
        licenceExpiry: '',
        preferredVehicle: '',
        makeModel: '',
        year: '',
        intendedUse: '',
        primaryUseArea: '',
        commencementDate: '',
        leasePeriod: '',
        leasePayment: '',
        leasePaymentFrequency: '',
        securityDeposit: '',
        maintenanceResponsibility: '',
        returnDate: '',
        purchasePrice: '',
        initialDeposit: '',
        balance: '',
        repaymentPeriod: '',
        repaymentAmount: '',
        repaymentFrequency: '',
        repaymentSource: '',
        monthlyIncome: '',
        guarantorName: '',
        guarantorPhone: '',
        guarantorAddress: '',
        guarantorOccupation: '',
        guarantorRelationship: '',
        guarantorIdType: '',
        guarantorIdNumber: '',
        applicantName: '',
        applicantSignature: '',
        applicantDate: '',
        guarantorNameSigned: '',
        guarantorSignature: '',
        guarantorDate: '',
        agreeTerms: false
      });
      setPhotoFile(null);
      setPhotoPreview('');
      setApplicantIdFile(null);
      setApplicantIdPreview('');
      setGuarantorIdFile(null);
      setGuarantorIdPreview('');
      setApplicationType('');
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormStatus(null);
    setUploadProgress(0);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      setUploadProgress(20);
      const photoUrl = await uploadFile(photoFile, 'passport-photos');
      setUploadProgress(40);
      const applicantIdUrl = await uploadFile(applicantIdFile, 'applicant-ids');
      setUploadProgress(60);
      const guarantorIdUrl = await uploadFile(guarantorIdFile, 'guarantor-ids');
      setUploadProgress(80);

      const { error } = await supabase
        .from('vehicle_applications')
        .insert([{
          application_type: applicationType,
          full_name: formData.fullName,
          business_name: formData.businessName,
          residential_address: formData.residentialAddress,
          phone: formData.phone,
          email: formData.email,
          occupation: formData.occupation,
          identification_type: formData.identificationType,
          id_number: formData.idNumber,
          drivers_licence: formData.driversLicence,
          licence_expiry: formData.licenceExpiry,
          preferred_vehicle: formData.preferredVehicle,
          make_model: formData.makeModel,
          year: formData.year,
          intended_use: formData.intendedUse,
          primary_use_area: formData.primaryUseArea,
          commencement_date: formData.commencementDate,
          lease_period: formData.leasePeriod,
          lease_payment: formData.leasePayment,
          lease_payment_frequency: formData.leasePaymentFrequency,
          security_deposit: formData.securityDeposit,
          maintenance_responsibility: formData.maintenanceResponsibility,
          return_date: formData.returnDate,
          purchase_price: formData.purchasePrice,
          initial_deposit: formData.initialDeposit,
          balance: formData.balance,
          repayment_period: formData.repaymentPeriod,
          repayment_amount: formData.repaymentAmount,
          repayment_frequency: formData.repaymentFrequency,
          repayment_source: formData.repaymentSource,
          monthly_income: formData.monthlyIncome,
          guarantor_name: formData.guarantorName,
          guarantor_phone: formData.guarantorPhone,
          guarantor_address: formData.guarantorAddress,
          guarantor_occupation: formData.guarantorOccupation,
          guarantor_relationship: formData.guarantorRelationship,
          guarantor_id_type: formData.guarantorIdType,
          guarantor_id_number: formData.guarantorIdNumber,
          applicant_name: formData.applicantName,
          applicant_signature: formData.applicantSignature,
          applicant_date: formData.applicantDate,
          guarantor_name_signed: formData.guarantorNameSigned,
          guarantor_signature: formData.guarantorSignature,
          guarantor_date: formData.guarantorDate,
          photo_url: photoUrl,
          applicant_id_url: applicantIdUrl,
          guarantor_id_url: guarantorIdUrl,
          status: 'pending'
        }]);

      if (error) throw error;

      setUploadProgress(100);
      setFormStatus('success');
      setPopupMessage({
        title: '✅ Application Submitted!',
        message: 'Your vehicle application has been successfully received. Our team will contact you within 48 hours.',
        type: 'success'
      });
      setShowPopup(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
      setPopupMessage({
        title: '❌ Submission Failed',
        message: 'There was a problem submitting your application. Please try again or contact us directly.',
        type: 'error'
      });
      setShowPopup(true);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="application-form-page">
      {/* POPUP MODAL */}
      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className={`popup-content ${popupMessage.type}`} onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon">
              {popupMessage.type === 'success' ? '✅' : '❌'}
            </div>
            <h2>{popupMessage.title}</h2>
            <p>{popupMessage.message}</p>
            <button className="popup-btn" onClick={closePopup}>
              {popupMessage.type === 'success' ? 'Continue' : 'Try Again'}
            </button>
          </div>
        </div>
      )}

      <section className="form-hero">
        <h1>ABVIG Vehicle Application Form</h1>
        <p>Single Vehicle Finance & Use Application</p>
      </section>

      <section className="form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-header">
              <h2>Application Type — Select ONE (Required)</h2>
              <div className="application-type-selector">
                <button
                  className={`type-btn ${applicationType === 'lease' ? 'active' : ''}`}
                  onClick={() => handleApplicationTypeChange('lease')}
                >
                  ☐ VEHICLE LEASE
                </button>
                <button
                  className={`type-btn ${applicationType === 'purchase' ? 'active' : ''}`}
                  onClick={() => handleApplicationTypeChange('purchase')}
                >
                  ☐ HIRE PURCHASE
                </button>
              </div>
              {!applicationType && (
                <p className="type-required">⚠️ Please select an application type to continue</p>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              {/* Application Date & No */}
              <div className="form-row two-col">
                <div className="form-group">
                  <label>Application Date *</label>
                  <input type="date" value={new Date().toISOString().split('T')[0]} disabled />
                </div>
                <div className="form-group">
                  <label>Application No.</label>
                  <input type="text" value={`ABVIG-${Date.now().toString().slice(-6)}`} disabled />
                </div>
              </div>

              {/* PASSPORT PHOTO */}
              <div className="form-section-title">
                <h3>APPLICANT PASSPORT PHOTO</h3>
              </div>

              <div className="photo-upload-section">
                <div className="photo-upload-container">
                  {photoPreview ? (
                    <div className="photo-preview">
                      <img src={photoPreview} alt="Applicant" />
                      <button
                        type="button"
                        className="remove-photo"
                        onClick={() => {
                          setPhotoFile(null);
                          setPhotoPreview('');
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="photo-placeholder">
                      <span className="photo-icon">📸</span>
                      <p>Upload Clear Passport</p>
                      <span className="photo-hint">Click to browse</span>
                    </div>
                  )}
                  <input
                    type="file"
                    id="photoUpload"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="photo-upload-btn"
                    onClick={() => document.getElementById('photoUpload').click()}
                  >
                    {photoPreview ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  <p className="photo-instruction">
                    ⚠️ Upload a <strong>clear passport photograph</strong> (JPEG/PNG, max 5MB)
                  </p>
                </div>
              </div>

              {/* 1. APPLICANT DETAILS */}
              <div className="form-section-title">
                <h3>1. APPLICANT DETAILS</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Full Name / Business Name *</label>
                  <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} required placeholder="Enter full name" />
                </div>
                <div className="form-group">
                  <label>Residential / Business Address *</label>
                  <input type="text" name="residentialAddress" value={formData.residentialAddress} onChange={handleInputChange} required placeholder="Enter address" />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} required placeholder="+234 800 000 0000" />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="email@example.com" />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Occupation / Business *</label>
                  <input type="text" name="occupation" value={formData.occupation} onChange={handleInputChange} required placeholder="Enter occupation" />
                </div>
                <div className="form-group">
                  <label>Means of Identification *</label>
                  <select name="identificationType" value={formData.identificationType} onChange={handleInputChange} required>
                    <option value="">Select ID Type</option>
                    <option value="National ID">National ID</option>
                    <option value="Driver's Licence">Driver's Licence</option>
                    <option value="International Passport">International Passport</option>
                  </select>
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>ID Number *</label>
                  <input type="text" name="idNumber" value={formData.idNumber} onChange={handleInputChange} required placeholder="Enter ID number" />
                </div>
                <div className="form-group">
                  <label>Driver's Licence Number</label>
                  <input type="text" name="driversLicence" value={formData.driversLicence} onChange={handleInputChange} placeholder="Enter driver's licence" />
                </div>
              </div>

              <div className="form-group">
                <label>Driver's Licence Expiry Date</label>
                <input type="date" name="licenceExpiry" value={formData.licenceExpiry} onChange={handleInputChange} />
              </div>

              {/* APPLICANT ID UPLOAD */}
              <div className="form-section-title">
                <h3>APPLICANT IDENTIFICATION DOCUMENT</h3>
              </div>

              <div className="id-upload-section">
                <div className="id-upload-container">
                  {applicantIdPreview ? (
                    <div className="id-preview">
                      <img src={applicantIdPreview} alt="Applicant ID" />
                      <button
                        type="button"
                        className="remove-id"
                        onClick={() => {
                          setApplicantIdFile(null);
                          setApplicantIdPreview('');
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="id-placeholder">
                      <span className="id-icon">🪪</span>
                      <p>Upload Clear ID Document</p>
                      <span className="id-hint">(National ID, Driver's Licence, or Passport)</span>
                    </div>
                  )}
                  <input
                    type="file"
                    id="applicantIdUpload"
                    accept="image/*"
                    onChange={handleApplicantIdChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="id-upload-btn"
                    onClick={() => document.getElementById('applicantIdUpload').click()}
                  >
                    {applicantIdPreview ? 'Change ID' : 'Upload ID'}
                  </button>
                  <p className="id-instruction">
                    ⚠️ Upload a <strong>clear picture of the ID used</strong> to verify the details with the number provided (JPEG/PNG, max 5MB)
                  </p>
                </div>
              </div>

              {/* 2. VEHICLE REQUEST */}
              <div className="form-section-title">
                <h3>2. VEHICLE REQUEST</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Preferred Vehicle *</label>
                  <input type="text" name="preferredVehicle" value={formData.preferredVehicle} onChange={handleInputChange} required placeholder="e.g., Toyota Hilux" />
                </div>
                <div className="form-group">
                  <label>Make / Model *</label>
                  <input type="text" name="makeModel" value={formData.makeModel} onChange={handleInputChange} required placeholder="e.g., Toyota Hilux 2024" />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Year</label>
                  <input type="text" name="year" value={formData.year} onChange={handleInputChange} placeholder="e.g., 2024" />
                </div>
                <div className="form-group">
                  <label>Intended Use *</label>
                  <select name="intendedUse" value={formData.intendedUse} onChange={handleInputChange} required>
                    <option value="">Select intended use</option>
                    <option value="Personal">Personal</option>
                    <option value="Business">Business</option>
                    <option value="Commercial Transportation">Commercial Transportation</option>
                    <option value="Corporate / Official Use">Corporate / Official Use</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Primary Area of Use</label>
                  <input type="text" name="primaryUseArea" value={formData.primaryUseArea} onChange={handleInputChange} placeholder="e.g., Lagos, Abuja" />
                </div>
                <div className="form-group">
                  <label>Expected Commencement Date</label>
                  <input type="date" name="commencementDate" value={formData.commencementDate} onChange={handleInputChange} />
                </div>
              </div>

              {/* 3. VEHICLE LEASE */}
              {applicationType === 'lease' && (
                <div className="conditional-section">
                  <div className="form-section-title lease-title">
                    <h3>3. VEHICLE LEASE</h3>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Preferred Lease Period *</label>
                      <select name="leasePeriod" value={formData.leasePeriod} onChange={handleInputChange} required>
                        <option value="">Select period</option>
                        <option value="1 Month">1 Month</option>
                        <option value="3 Months">3 Months</option>
                        <option value="6 Months">6 Months</option>
                        <option value="12 Months">12 Months</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Lease Payment (₦) *</label>
                      <input type="number" name="leasePayment" value={formData.leasePayment} onChange={handleInputChange} required placeholder="0.00" />
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Payment Frequency</label>
                      <select name="leasePaymentFrequency" value={formData.leasePaymentFrequency} onChange={handleInputChange}>
                        <option value="">Select frequency</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Security Deposit (₦)</label>
                      <input type="number" name="securityDeposit" value={formData.securityDeposit} onChange={handleInputChange} placeholder="0.00" />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Maintenance Responsibility</label>
                    <select name="maintenanceResponsibility" value={formData.maintenanceResponsibility} onChange={handleInputChange}>
                      <option value="">Select responsibility</option>
                      <option value="Applicant">Applicant</option>
                      <option value="ABVIG">ABVIG</option>
                      <option value="As stated in Lease Agreement">As stated in Lease Agreement</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Expected Vehicle Return Date</label>
                    <input type="date" name="returnDate" value={formData.returnDate} onChange={handleInputChange} />
                  </div>

                  <div className="legal-notice">
                    <p>⚠️ I understand that the vehicle remains the property of ABVIG throughout the lease period and must be returned in accordance with the applicable Lease Agreement.</p>
                  </div>
                </div>
              )}

              {/* 4. HIRE PURCHASE */}
              {applicationType === 'purchase' && (
                <div className="conditional-section">
                  <div className="form-section-title purchase-title">
                    <h3>4. HIRE PURCHASE</h3>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Vehicle Purchase Price (₦) *</label>
                      <input type="number" name="purchasePrice" value={formData.purchasePrice} onChange={handleInputChange} required placeholder="0.00" />
                    </div>
                    <div className="form-group">
                      <label>Initial Deposit (₦) *</label>
                      <input type="number" name="initialDeposit" value={formData.initialDeposit} onChange={handleInputChange} required placeholder="0.00" />
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Balance (₦)</label>
                      <input type="number" name="balance" value={formData.balance} onChange={handleInputChange} placeholder="0.00" />
                    </div>
                    <div className="form-group">
                      <label>Repayment Period *</label>
                      <input type="text" name="repaymentPeriod" value={formData.repaymentPeriod} onChange={handleInputChange} required placeholder="e.g., 12 months" />
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Repayment Amount (₦) *</label>
                      <input type="number" name="repaymentAmount" value={formData.repaymentAmount} onChange={handleInputChange} required placeholder="0.00" />
                    </div>
                    <div className="form-group">
                      <label>Payment Frequency</label>
                      <select name="repaymentFrequency" value={formData.repaymentFrequency} onChange={handleInputChange}>
                        <option value="">Select frequency</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row two-col">
                    <div className="form-group">
                      <label>Source of Repayment</label>
                      <select name="repaymentSource" value={formData.repaymentSource} onChange={handleInputChange}>
                        <option value="">Select source</option>
                        <option value="Salary">Salary</option>
                        <option value="Business Income">Business Income</option>
                        <option value="Contract/Professional Income">Contract/Professional Income</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Estimated Monthly Income (₦)</label>
                      <input type="number" name="monthlyIncome" value={formData.monthlyIncome} onChange={handleInputChange} placeholder="0.00" />
                    </div>
                  </div>

                  <div className="legal-notice">
                    <p>⚠️ I understand that completion of this application does not by itself transfer ownership of the vehicle to me. Ownership shall be governed by the formal Hire Purchase Agreement executed with ABVIG.</p>
                  </div>
                </div>
              )}

              {/* 5. GUARANTOR */}
              <div className="form-section-title">
                <h3>5. GUARANTOR / REFERENCE</h3>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Guarantor Full Name *</label>
                  <input type="text" name="guarantorName" value={formData.guarantorName} onChange={handleInputChange} required placeholder="Enter guarantor name" />
                </div>
                <div className="form-group">
                  <label>Guarantor Phone *</label>
                  <input type="tel" name="guarantorPhone" value={formData.guarantorPhone} onChange={handleInputChange} required placeholder="+234 800 000 0000" />
                </div>
              </div>

              <div className="form-group">
                <label>Guarantor Address</label>
                <input type="text" name="guarantorAddress" value={formData.guarantorAddress} onChange={handleInputChange} placeholder="Enter address" />
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Guarantor Occupation</label>
                  <input type="text" name="guarantorOccupation" value={formData.guarantorOccupation} onChange={handleInputChange} placeholder="Enter occupation" />
                </div>
                <div className="form-group">
                  <label>Relationship to Applicant</label>
                  <input type="text" name="guarantorRelationship" value={formData.guarantorRelationship} onChange={handleInputChange} placeholder="e.g., Family, Colleague" />
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-group">
                  <label>Guarantor ID Type</label>
                  <select name="guarantorIdType" value={formData.guarantorIdType} onChange={handleInputChange}>
                    <option value="">Select ID Type</option>
                    <option value="National ID">National ID</option>
                    <option value="Driver's Licence">Driver's Licence</option>
                    <option value="International Passport">International Passport</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Guarantor ID Number</label>
                  <input type="text" name="guarantorIdNumber" value={formData.guarantorIdNumber} onChange={handleInputChange} placeholder="Enter ID number" />
                </div>
              </div>

              {/* GUARANTOR ID UPLOAD */}
              <div className="form-section-title">
                <h3>GUARANTOR IDENTIFICATION DOCUMENT</h3>
              </div>

              <div className="id-upload-section">
                <div className="id-upload-container">
                  {guarantorIdPreview ? (
                    <div className="id-preview">
                      <img src={guarantorIdPreview} alt="Guarantor ID" />
                      <button
                        type="button"
                        className="remove-id"
                        onClick={() => {
                          setGuarantorIdFile(null);
                          setGuarantorIdPreview('');
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="id-placeholder">
                      <span className="id-icon">🪪</span>
                      <p>Upload Guarantor ID Document</p>
                      <span className="id-hint">(National ID, Driver's Licence, or Passport)</span>
                    </div>
                  )}
                  <input
                    type="file"
                    id="guarantorIdUpload"
                    accept="image/*"
                    onChange={handleGuarantorIdChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="id-upload-btn"
                    onClick={() => document.getElementById('guarantorIdUpload').click()}
                  >
                    {guarantorIdPreview ? 'Change ID' : 'Upload ID'}
                  </button>
                  <p className="id-instruction">
                    ⚠️ Upload a <strong>clear picture of the ID used</strong> to verify the guarantor details with the number provided (JPEG/PNG, max 5MB)
                  </p>
                </div>
              </div>

              {/* 6. DECLARATION */}
              <div className="form-section-title">
                <h3>6. APPLICANT UNDERTAKING & DECLARATION</h3>
              </div>

              <div className="declaration-text">
                <p>I declare that all information provided is true, accurate and complete. I understand that ABVIG may verify the information and documents provided and may reject this application where information is false, incomplete or cannot be verified.</p>
                <p>I agree that any vehicle offered to me shall be used responsibly and only for lawful purposes. I shall not sell, sublease, transfer, pledge, assign, alter, conceal, abandon or dispose of the vehicle without ABVIG's prior written consent.</p>
                <p>I understand that I will be responsible for all payments and obligations applicable to the arrangement approved by ABVIG.</p>
                <p>I authorize ABVIG to verify the information and documents supplied by me for the purpose of assessing this application.</p>
                <p>I understand that this application is not an approval, sale, lease or transfer of ownership. The arrangement shall only become effective upon ABVIG's approval and execution of the appropriate formal agreement.</p>
                <p>I confirm that I have read and understood this declaration.</p>
              </div>

              <div className="form-row three-col">
                <div className="form-group">
                  <label>Applicant's Full Name *</label>
                  <input type="text" name="applicantName" value={formData.applicantName} onChange={handleInputChange} required placeholder="Full name" />
                </div>
                <div className="form-group">
                  <label>Signature</label>
                  <input type="text" name="applicantSignature" value={formData.applicantSignature} onChange={handleInputChange} placeholder="Digital signature" />
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" name="applicantDate" value={formData.applicantDate} onChange={handleInputChange} required />
                </div>
              </div>

              {/* 7. GUARANTOR DECLARATION */}
              <div className="form-section-title">
                <h3>7. GUARANTOR DECLARATION</h3>
              </div>

              <div className="declaration-text">
                <p>I confirm that I know the applicant and have provided my details as a guarantor/reference for this application.</p>
                <p>I authorize ABVIG to verify the information I have provided.</p>
                <p>Where I sign a separate guarantee or other undertaking with ABVIG, I agree to be bound by the terms of that document.</p>
              </div>

              <div className="form-row three-col">
                <div className="form-group">
                  <label>Guarantor's Full Name</label>
                  <input type="text" name="guarantorNameSigned" value={formData.guarantorNameSigned} onChange={handleInputChange} placeholder="Full name" />
                </div>
                <div className="form-group">
                  <label>Signature</label>
                  <input type="text" name="guarantorSignature" value={formData.guarantorSignature} onChange={handleInputChange} placeholder="Digital signature" />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" name="guarantorDate" value={formData.guarantorDate} onChange={handleInputChange} />
                </div>
              </div>

              {/* Upload Progress */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="upload-progress-container">
                  <div className="upload-progress-bar">
                    <div className="upload-progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                  <span className="upload-progress-text">{uploadProgress}%</span>
                </div>
              )}

              {/* Terms */}
              <div className="form-group checkbox">
                <label className="checkbox-label">
                  <input type="checkbox" name="agreeTerms" checked={formData.agreeTerms} onChange={handleInputChange} required />
                  I confirm that I have read and understood all terms and conditions.
                </label>
              </div>

              <button type="submit" className="btn-gold submit-btn" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VehicleApplicationForm;